import {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import Grid from '@mui/material/Grid'
import {Close} from '@mui/icons-material'
import {Box, Checkbox, Chip, ClickAwayListener, FormControlLabel, InputAdornment} from '@mui/material'
import {StyledTextField, StyledPopperWide} from '../../common/MulitSelectDropdown/MultiSelectDropdownComponentStyle'
import {useFilterConfig} from '../../../store/hooks/config'
import {
  DomainHeading,
  SelectedHeaderRow,
  StyledBox,
  StyledGridItem,
  StyledInfoOutlinedIcon,
  StyledSelectAllFormControl,
  StyledSubTopic,
  StyledToolTip,
  StyledTopic,
  UseCaseChipsWrapper,
  UseCaseSelectedItemsContainer,
  StyledUseCaseFormControl,
  StyledUseCaseFormControlLabel,
  StyledDeleteIconButton,
  StyledDropdownContainer,
  StyledKeyboardArrowDownIcon,
  StyledUseCaseCustomLabels,
  InputTitleItalic
} from './UseCaseStyles'

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
const checkedIcon = <CheckBoxIcon fontSize='small' />

const UseCaseDropdownPanel = ({
  domain,
  topics,
  selectedUseCases,
  handleUpdateSelectedUseCases,
  showCustomLabels,
  onCustomizeLabels
}) => {
  const filterConfig = useFilterConfig()
  const [topicsData, setTopicsData] = useState([])
  const [open, setOpen] = useState(false)
  const [lastClickedUseCaseName, setLastClickedUseCaseName] = useState('')

  useEffect(() => {
    setTopicsData(topics)
  }, [topics])

  const handleClose = () => {
    setOpen(false)
  }

  const handleClick = () => {
    setOpen(!open)
  }

  const autocompleteRef = useRef(null)
  useEffect(() => {
    open && autocompleteRef.current?.focus()
  }, [open])

  const handleUseCaseSelection = (topicIndex, subTopicIndex, useCaseIndex) => (event) => {
    const newTopicsData = [...topicsData]
    const clickedUseCaseName = newTopicsData[topicIndex].subTopics[subTopicIndex].useCases[useCaseIndex].name
    newTopicsData[topicIndex].subTopics[subTopicIndex].useCases[useCaseIndex].selected = event.target.checked
    setLastClickedUseCaseName(clickedUseCaseName)
    setTopicsData(newTopicsData)
    handleUpdateSelectedUseCases()
  }

  const getPills = (domainName, selUseCases) => {
    return selUseCases.map((obj) => {
      return (
        <Chip
          variant='variant1'
          key={domainName + obj.id}
          label={obj.name}
          aria-label={obj.name}
          deleteIcon={
            <StyledDeleteIconButton aria-label={`Delete ${obj.name}`}>
              <Close
                variant='closeIcon'
                onKeyDown={(event) => onChipDelete(event, obj.id)}
                data-testid={`Delete ${obj.name}`}
              />
            </StyledDeleteIconButton>
          }
          onDelete={(event) => onChipDelete(event, obj.id)}
        />
      )
    })
  }

  const handleUseCaseUpdateWhenChipDeletion = (subTopic, chipId) => {
    for (const useCase of subTopic.useCases) {
      if (useCase.selected && useCase.id === chipId) {
        useCase.selected = false
      }
    }
  }

  const onChipDelete = (event, chipId) => {
    if ((event.key === 'Enter' && event.type === 'keydown') || event.type === 'click') {
      const newTopicsData = [...topicsData]
      for (const topic of newTopicsData) {
        for (const subTopic of topic.subTopics) {
          handleUseCaseUpdateWhenChipDeletion(subTopic, chipId)
        }
      }
      setTopicsData(newTopicsData)
      handleUpdateSelectedUseCases()
    }
  }

  const handleUseCaseUpdateWhenSelectAll = (subTopic, event) => {
    for (const useCase of subTopic.useCases) {
      if (!useCase.disabled) {
        useCase.selected = event.target.checked
      }
    }
  }

  const handleSelectAllUseCases = (event) => {
    const newTopicsData = [...topicsData]
    for (const topic of newTopicsData) {
      for (const subTopic of topic.subTopics) {
        handleUseCaseUpdateWhenSelectAll(subTopic, event)
      }
    }
    setTopicsData(newTopicsData)
    handleUpdateSelectedUseCases()
  }

  const handleUseCaseCheckboxKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.target.click()
    } else if (event.key === 'Escape') {
      handleClose()
    }
  }
  const handleUpdateAllSelectedObj = (subTopic, allSelectedObj) => {
    for (const useCase of subTopic.useCases) {
      if (!useCase.disabled && !useCase.selected) {
        allSelectedObj.value = false
      }
    }
  }

  const isAllUseCasesSelected = () => {
    const allSelectedObj = {value: true}
    for (const topic of topicsData) {
      for (const subTopic of topic.subTopics) {
        handleUpdateAllSelectedObj(subTopic, allSelectedObj)
      }
    }
    return allSelectedObj.value && !isAllDisabled()
  }

  const handleUpdateAllDisabledObj = (subTopic, allDisabledObj) => {
    for (const useCase of subTopic.useCases) {
      if (!useCase.disabled) {
        allDisabledObj.value = false
      }
    }
  }

  const isAllDisabled = () => {
    const allDisabledObj = {value: true}
    for (const topic of topicsData) {
      for (const subTopic of topic.subTopics) {
        handleUpdateAllDisabledObj(subTopic, allDisabledObj)
      }
    }
    return allDisabledObj.value
  }

  const getFilterNameFromKey = (filterKey) => {
    if (!filterConfig?.data) return filterKey
    for (const section of filterConfig.data) {
      const filter = section.filters?.find((f) => f.key === filterKey)
      if (filter) return filter.name
    }
    return filterKey
  }

  const getToolTipTitle = (useCaseObj) => {
    return useCaseObj.filters?.map((filter) => {
      const filterName = getFilterNameFromKey(filter.filterKey)
      return <Box key={useCaseObj.id + filter.filterKey}>{filterName}</Box>
    })
  }

  const getTotalUseCaseCount = () => {
    return topicsData
      .flatMap((topic) => topic.subTopics)
      .flatMap((subTopic) => subTopic.useCases)
      .filter((useCase) => !useCase.disabled).length
  }

  const getSelectedUseCaseCount = () => {
    const domainSelection = selectedUseCases?.find((obj) => obj.domain === domain)
    return domainSelection ? domainSelection.useCases.length : 0
  }

  const getDropdownValue = () => {
    const selectedCount = getSelectedUseCaseCount()
    const totalCount = getTotalUseCaseCount()

    if (selectedCount === 0) {
      return 'Select'
    } else if (selectedCount === totalCount && totalCount > 0) {
      return 'Selected All'
    } else {
      return `Selected (${selectedCount})`
    }
  }

  const id = open ? 'admin-label' : undefined
  const domainSelection = selectedUseCases?.find((obj) => obj.domain === domain) || {domain, useCases: []}

  return (
    <Box>
      <DomainHeading>{domain}</DomainHeading>
      <Grid container spacing={3}>
        <StyledGridItem item md={12} lg={6}>
          <StyledDropdownContainer>
            <StyledTextField
              label={domain}
              value={getDropdownValue()}
              variant='outlined'
              onClick={handleClick}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  open && handleClose()
                }
              }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position='end'>
                    <StyledKeyboardArrowDownIcon />
                  </InputAdornment>
                )
              }}
              aria-describedby={id}
            />
            {open && (
              <ClickAwayListener onClickAway={handleClose}>
                <StyledPopperWide ref={autocompleteRef} id={id} placement='bottom'>
                  <StyledSelectAllFormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault()
                              event.target.click()
                            } else if (event.key === 'Escape') {
                              handleClose()
                            }
                          }}
                          icon={icon}
                          checkedIcon={checkedIcon}
                          disabled={isAllDisabled()}
                          checked={isAllUseCasesSelected()}
                          onClick={handleSelectAllUseCases}
                        />
                      }
                      label={<span>Select All Use Cases</span>}
                    />
                  </StyledSelectAllFormControl>
                  {topicsData
                    ? topicsData.map((topicObj, topicIndex) => {
                        return (
                          <Box key={topicObj.name}>
                            <StyledTopic variant='subtitle1'>{topicObj.name}</StyledTopic>
                            {topicObj.subTopics.map((subTopicObj, subTopicIndex) => {
                              return (
                                <Box key={topicObj.name + subTopicObj.name}>
                                  <StyledSubTopic variant='subtitle1'>{subTopicObj.name}</StyledSubTopic>
                                  {subTopicObj.useCases.map((useCaseObj, useCaseIndex) => {
                                    return (
                                      <StyledBox key={topicObj.name + subTopicObj.name + useCaseObj.name}>
                                        <StyledUseCaseFormControl isAdminPanel>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                checked={useCaseObj.selected}
                                                disabled={useCaseObj.disabled}
                                                onKeyDown={handleUseCaseCheckboxKeyDown}
                                                onClick={handleUseCaseSelection(
                                                  topicIndex,
                                                  subTopicIndex,
                                                  useCaseIndex
                                                )}
                                              />
                                            }
                                            label={
                                              <StyledUseCaseFormControlLabel
                                                disabled={useCaseObj.disabled}
                                                isAdminPanel
                                              >
                                                {useCaseObj.name}
                                              </StyledUseCaseFormControlLabel>
                                            }
                                          />
                                          <StyledToolTip title={getToolTipTitle(useCaseObj)}>
                                            <StyledInfoOutlinedIcon className='info-icon' />
                                          </StyledToolTip>
                                        </StyledUseCaseFormControl>
                                      </StyledBox>
                                    )
                                  })}
                                </Box>
                              )
                            })}
                          </Box>
                        )
                      })
                    : null}
                </StyledPopperWide>
              </ClickAwayListener>
            )}
          </StyledDropdownContainer>
        </StyledGridItem>
        <StyledGridItem item md={12} lg={6}>
          <Box key={domainSelection.domain}>
            <UseCaseSelectedItemsContainer>
              <SelectedHeaderRow>
                <InputTitleItalic tabIndex={0}>
                  Selected {domainSelection.domain} Use Cases ({domainSelection.useCases.length})
                </InputTitleItalic>
                {showCustomLabels && (
                  <StyledUseCaseCustomLabels
                    variant='text'
                    onClick={() => onCustomizeLabels?.(domain, lastClickedUseCaseName)}
                    data-testid={`customize-labels-${domain}`}
                  >
                    Customize Labels
                  </StyledUseCaseCustomLabels>
                )}
              </SelectedHeaderRow>
              <UseCaseChipsWrapper>{getPills(domain, domainSelection.useCases)}</UseCaseChipsWrapper>
            </UseCaseSelectedItemsContainer>
          </Box>
        </StyledGridItem>
      </Grid>
    </Box>
  )
}

UseCaseDropdownPanel.propTypes = {
  domain: PropTypes.string.isRequired,
  topics: PropTypes.array.isRequired,
  selectedUseCases: PropTypes.array.isRequired,
  handleUpdateSelectedUseCases: PropTypes.func.isRequired,
  showCustomLabels: PropTypes.bool,
  onCustomizeLabels: PropTypes.func
}

export default UseCaseDropdownPanel

/* Function Descriptions:
   - UseCaseDropdownPanel: This component renders a dropdown panel for selecting use cases within a specific domain. 
     It allows users to select or deselect use cases, view selected use cases, and provides tooltips with additional information.
   - handleUseCaseSelection: Updates the selection state of a specific use case when clicked.
   - handleSelectAllUseCases: Selects or deselects all use cases within the dropdown.
   - getPills: Generates chips for selected use cases.
   - onChipDelete: Handles the deletion of selected use case chips.
   - isAllUseCasesSelected: Checks if all use cases are selected.
   - isAllDisabled: Checks if all use cases are disabled.
   - getToolTipTitle: Returns the tooltip content for a specific use case.
*/
